import {StyledComponent} from '@emotion/styled';
import {observer} from 'mobx-react-lite';
import React from 'react';

function originalMemo<P extends object, TRef = unknown>(
  Component: React.ForwardRefRenderFunction<TRef, P>,
  forwardRef: true
): React.ForwardRefExoticComponent<
  React.PropsWithoutRef<P> & React.RefAttributes<TRef>
>;

function originalMemo<P extends object, TRef = unknown>(
  Component: React.ForwardRefRenderFunction<TRef, P>
): React.FunctionComponent<P>;

function originalMemo<P extends object, TRef = unknown>(
  Component: React.ForwardRefRenderFunction<TRef, P>,
  forwardRef = false
):
  | React.ForwardRefExoticComponent<
      React.PropsWithoutRef<P> & React.RefAttributes<TRef>
    >
  | React.FunctionComponent<P> {
  if (forwardRef) {
    const ObserverComponent = observer(React.forwardRef(Component));

    const TranslatableComponent = (
      props: any,
      ref: React.Ref<TRef>
    ): React.ReactElement => <ObserverComponent ref={ref} {...props} />;

    return React.forwardRef(TranslatableComponent);
  } else {
    const ObserverComponent = observer(Component);

    const TranslatableComponent = (props: P): React.ReactElement => (
      <ObserverComponent {...props} />
    );

    return TranslatableComponent;
  }
}

/**
 * @deprecated Use `observer` from `mobx-react-lite` instead.
 *
 * Provides a memoized version of "component".
 * If "styledComponent" is provided, the output will also be an styled component which can be used in Emotion styles.
 * If "forwardRef" is true, the output will also forward ref to the component (like original "React.forwardRef").
 * Regardless of the parameters, the output "mobx.observer" is applied to the component so that it automatically
 * reflects changes in mobx stores.
 * Regardless of the parameters, it will always rerender content when language changes to reflect client side language
 * changes without refreshing the page.
 */
function styledMemo<T>(component: T): T;
function styledMemo<T, U extends object>(
  component: T,
  styledComponent: StyledComponent<U>
): T & StyledComponent<U>;
function styledMemo<T>(component: T, forwardRef: true): T;
function styledMemo<T, U extends object>(
  component: T,
  styledComponent: StyledComponent<U>,
  forwardRef: boolean
): T & StyledComponent<U>;

/** @deprecated Use `observer` from `mobx-react-lite` instead. */
function styledMemo<T, U extends object>(
  component: T,
  styledComponent?: StyledComponent<U> | boolean,
  forwardRef = false
): T | (T & StyledComponent<U>) {
  const typedMemo: <V>(component: V, forwardRef: boolean) => V = originalMemo;

  if (styledComponent == null) {
    return typedMemo(component, forwardRef);
  } else if (typeof styledComponent === 'boolean') {
    return typedMemo(component, styledComponent);
  } else {
    const typedWithComponent: <V>(component: V) => V & StyledComponent<U> =
      styledComponent.withComponent;

    return typedWithComponent(typedMemo(component, forwardRef));
  }
}

/** @deprecated Use `observer` from `mobx-react-lite` instead. */
export const memo = styledMemo;
